import type {
    BandMember,
    BandProfile,
    JourneyFrame,
    NavSection,
    SongTrack
} from '@/types'

// Every piece of copy on the page lives here. Song titles stay in Malay, they are names.
// Ages and roles follow the band's portfolio. Swap `photo: null` for a real photo when it is supplied.

export const band: BandProfile = {
    id: 'bluntsheep',
    name: 'The Bluntsheep',
    tagline: ['Blunt is light', 'Blunt is tight', 'Choose your path right'],
    foundedYear: 2023,
    founded: 'Late 2023',
    origin: 'Malaysia',
    genres: ['Pop Rock', 'Alternative Rock', '90s Grunge', 'Garage Rock'],
    summary: "Pop rock from Malaysia, formed in late 2023 by four friends.",
    shortBio: "The Bluntsheep is a pop rock band formed in late 2023 by a group of friends in Malaysia. Their sound pairs big pop rock hooks with an alternative edge and the grit of 90s grunge, played by four musicians with very different influences.",
    musicIdentityText: "The Bluntsheep play pop rock coloured by alternative rock and the raw grain of 90s grunge. Every song carries something to say. Hentikan stands up against bullying, Di Puncak Dunia is written out of love for the nation, and Cinta Paling Hebat is about the greatest love of all, love for the One.",
    journeyText: "On stage the band runs on charisma. A sequencer adds a modern layer on top of a garage rock soul, while vocals, guitar, drums and bass lock in tight underneath it.",
    visionStatement: 'From Malaysia to the world.',
    visionText: "The vision is to build a community of listeners that starts at home in Malaysia, grows across the region, and keeps going out into the world. One stage, one crowd, one city at a time.",
    socialLinks: {
        instagram: 'https://www.instagram.com/thebluntsheep/?hl=en',
        instagramHandle: '@thebluntsheep',
        pressKitUrl: null
    }
}

export const members: BandMember[] = [
    {
        id: 'atta-rumnan',
        order: 1,
        fullName: 'Attana Qawiuddin Rumnan',
        stageName: 'Attana Rumnan',
        shortName: 'Atta',
        age: 19,
        primaryRole: 'Guitarist & Vocalist',
        secondarySkills: ['Songwriting', 'Painting'],
        influences: ['Rock', 'Metal', 'Classic Rock', 'Grunge'],
        biography: "Atta fronts the band on guitar and vocals. He grew up on rock, metal, classic rock and grunge, and he has a real gift for writing songs. Away from the mic, he paints.",
        layoutAlignment: 'left',
        instrument: 'les-paul',
        instrumentLabel: 'Les Paul',
        sample: {
            label: "Play Atta's riff",
            synth: 'sample-riff',
            url: null,
            durationSeconds: 4
        },
        photo: null
    },
    {
        id: 'moses-dass',
        order: 2,
        fullName: 'Moses Dass',
        stageName: 'Moses Dass',
        shortName: 'Moses',
        age: 20,
        primaryRole: 'Drummer',
        secondarySkills: ['Keyboard', 'Guitar', 'Bass'],
        influences: ['Jazz', 'R&B'],
        biography: "Moses holds down the drums. He is a multi-instrumentalist who also plays keyboard, guitar and bass, and his feel behind the kit comes from jazz and R&B.",
        layoutAlignment: 'right',
        instrument: 'drum-kit',
        instrumentLabel: 'Drum kit',
        sample: {
            label: "Play Moses' fill",
            synth: 'sample-fill',
            url: null,
            durationSeconds: 3
        },
        photo: null
    },
    {
        id: 'umar-naufal',
        order: 3,
        fullName: 'Umar Naufal',
        stageName: 'Umar Naufal',
        shortName: 'Umar',
        age: 18,
        primaryRole: 'Lead Guitarist',
        secondarySkills: [],
        influences: ['Metal', 'Classic Rock'],
        biography: "Umar plays lead guitar. He has been playing since he was 12, and his lines and solos draw on metal and classic rock.",
        layoutAlignment: 'left',
        instrument: 'telecaster',
        instrumentLabel: 'Telecaster',
        sample: {
            label: "Play Umar's lead",
            synth: 'sample-lead',
            url: null,
            durationSeconds: 4
        },
        photo: null
    },
    {
        id: 'arif-azmi',
        order: 4,
        fullName: 'Arif Azmi',
        stageName: 'Arif Azmi',
        shortName: 'Arif',
        age: 18,
        primaryRole: 'Bassist',
        secondarySkills: ['Ukulele'],
        influences: ['Alternative Rock', 'Punk'],
        biography: "Arif plays bass. He started out on the ukulele at the age of six before moving to four strings, and he brings alternative rock and punk energy to the low end.",
        layoutAlignment: 'right',
        instrument: 'bass',
        instrumentLabel: '4-string bass',
        sample: {
            label: "Play Arif's groove",
            synth: 'sample-groove',
            url: null,
            durationSeconds: 4
        },
        photo: null
    }
]

export const tracks: SongTrack[] = [
    {
        id: 'hentikan',
        number: '01',
        title: 'Hentikan',
        theme: 'A stand against bullying',
        themeOriginal: 'Isu Buli',
        previewAudioUrl: null,
        externalStreamUrl: null,
        durationSeconds: 24,
        synth: 'track-hentikan'
    },
    {
        id: 'di-puncak-dunia',
        number: '02',
        title: 'Di Puncak Dunia',
        theme: 'Love for the nation',
        themeOriginal: 'Cinta Terhadap Negara',
        previewAudioUrl: null,
        externalStreamUrl: null,
        durationSeconds: 24,
        synth: 'track-di-puncak-dunia'
    },
    {
        id: 'cinta-paling-hebat',
        number: '03',
        title: 'Cinta Paling Hebat',
        theme: 'Love for the One',
        themeOriginal: 'Cinta Kepada Yang Esa',
        previewAudioUrl: null,
        externalStreamUrl: null,
        durationSeconds: 24,
        synth: 'track-cinta-paling-hebat'
    }
]

export const journeyFrames: JourneyFrame[] = [
    {
        id: 'charisma',
        number: '01',
        title: 'Stage charisma',
        caption: 'The live show runs on charisma, from the first count-in to the last chord.',
        art: 'mic',
        photo: null
    },
    {
        id: 'garage',
        number: '02',
        title: 'Garage rock soul',
        caption: 'Underneath everything sits a garage rock soul, raw and loud.',
        art: 'amp',
        photo: null
    },
    {
        id: 'sequencer',
        number: '03',
        title: 'The modern layer',
        caption: 'A sequencer runs under the live band, a modern layer on top of the garage rock core.',
        art: 'sequencer',
        photo: null
    },
    {
        id: 'vocals',
        number: '04',
        title: 'Vocals',
        caption: 'Atta sings and plays guitar at the front of the stage.',
        art: 'les-paul',
        photo: null
    },
    {
        id: 'guitar',
        number: '05',
        title: 'Guitar',
        caption: 'Umar on lead guitar, built on metal and classic rock.',
        art: 'telecaster',
        photo: null
    },
    {
        id: 'drums',
        number: '06',
        title: 'Drums',
        caption: 'Moses on drums, with a feel that comes from jazz and R&B.',
        art: 'drum-kit',
        photo: null
    },
    {
        id: 'bass',
        number: '07',
        title: 'Bass',
        caption: 'Arif on bass, with alternative rock and punk in his playing.',
        art: 'bass',
        photo: null
    },
    {
        id: 'tight',
        number: '08',
        title: 'Blunt is tight',
        caption: 'Vocals, guitar, drums and bass, locked in as one band.',
        art: 'crest',
        photo: null
    }
]

export const navSections: NavSection[] = [
    { id: 'about', label: 'About' },
    { id: 'members', label: 'Members' },
    { id: 'music', label: 'Music' },
    { id: 'journey', label: 'Journey' },
    { id: 'vision', label: 'Vision' }
]
